import React from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FormFieldLabel from './FormFieldLabel';
import ImageUpload from './ImageUpload';
import { fieldSx, characterCountSx, BRAND_ORANGE, mergeFieldSx } from './manageEventFieldStyles';
import { DESCRIPTION_MAX } from '../manageEventForm';
import { dishImageFieldKey, dishNameFieldKey } from '../menuDishValidation';
import { validateDishImageFile } from '../dishImageValidation';
import {
  DISH_IMAGE_ACCEPT,
  DISH_IMAGE_DIMENSION_HINT,
  DISH_IMAGE_FORMAT_HINT,
  DISH_IMAGE_PREVIEW_SIZE,
} from '../eventFormConstants';

const compactFieldSx = {
  ...fieldSx,
  '& .MuiOutlinedInput-root': {
    ...fieldSx['& .MuiOutlinedInput-root'],
    fontSize: '0.8125rem',
  },
};

const CharacterCount = ({ current, max }) => (
  <Typography component="span" sx={{ ...characterCountSx, mt: 0.25 }}>
    {current}/{max}
  </Typography>
);

const MenuCourseSection = ({
  section,
  sectionIndex,
  onUpdateSection,
  onAddDish,
  onRemoveDish,
  isLast,
  errors = {},
  disabled = false,
}) => {
  const updateDish = (dishIndex, field, value) => {
    const dishes = section.dishes.map((dish, i) =>
      i === dishIndex ? { ...dish, [field]: value } : dish,
    );
    onUpdateSection(sectionIndex, dishes);
  };

  const updateDishImage = (dishIndex, file, preview) => {
    const dishes = section.dishes.map((dish, i) =>
      i === dishIndex ? { ...dish, imageFile: file ?? null, imagePreview: preview ?? '' } : dish,
    );
    onUpdateSection(sectionIndex, dishes);
  };

  return (
    <Box
      sx={{
        mb: isLast ? 0 : 3,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: section.dishes.length > 0 ? 1.25 : 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary' }}
        >
          {section.course}
        </Typography>
        <Typography
          variant="body2"
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => onAddDish(sectionIndex)}
          sx={{
            color: BRAND_ORANGE,
            fontWeight: 600,
            fontSize: '0.8125rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            border: 'none',
            background: 'none',
            padding: 0,
            fontFamily: 'inherit',
            opacity: disabled ? 0.5 : 1,
            '&:hover': { opacity: disabled ? 0.5 : 0.85 },
          }}
        >
          + Add Dish
        </Typography>
      </Box>

      {section.dishes.length === 0 ? (
        <Box
          sx={{
            py: 2,
            px: 1.5,
            border: '1px dashed',
            borderColor: 'rgba(224, 104, 10, 0.35)',
            borderRadius: 1.5,
            bgcolor: (theme) => theme.portal?.accentSoft ?? 'action.hover',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            No dishes added yet
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.25,
          }}
        >
          {section.dishes.map((dish, dishIndex) => {
            const nameError = errors[dishNameFieldKey(sectionIndex, dishIndex)];
            const imageError = errors[dishImageFieldKey(sectionIndex, dishIndex)];

            return (
              <Box
                key={dish.id}
                sx={{
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'rgba(224, 104, 10, 0.22)',
                  borderRadius: 1.5,
                  bgcolor: (theme) => theme.portal?.accentSoft ?? 'action.hover',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: BRAND_ORANGE,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {dishIndex + 1}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.primary', flex: 1 }}
                  >
                    Dish {dishIndex + 1}
                  </Typography>
                  <IconButton
                    aria-label="Remove dish"
                    onClick={() => onRemoveDish(sectionIndex, dishIndex)}
                    size="small"
                    sx={{
                      color: '#f44336',
                      border: '1px solid #f44336',
                      borderRadius: 1,
                      width: 28,
                      height: 28,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.main',
                      },
                    }}
                  >
                    <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormFieldLabel required sx={{ mb: 0.25, fontSize: '0.8125rem' }}>
                      Dish Name
                    </FormFieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add"
                      value={dish.dishName}
                      onChange={(e) => updateDish(dishIndex, 'dishName', e.target.value)}
                      error={Boolean(nameError)}
                      helperText={nameError}
                      sx={{ ...mergeFieldSx(Boolean(nameError)), ...compactFieldSx, mb: 1 }}
                    />

                    <FormFieldLabel sx={{ mb: 0.25, fontSize: '0.8125rem' }}>
                      Description
                    </FormFieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Add Description"
                      value={dish.description}
                      onChange={(e) =>
                        updateDish(
                          dishIndex,
                          'description',
                          e.target.value.slice(0, DESCRIPTION_MAX),
                        )
                      }
                      sx={compactFieldSx}
                    />
                    <CharacterCount current={dish.description.length} max={DESCRIPTION_MAX} />
                  </Box>

                  <Box sx={{ flexShrink: 0, width: DISH_IMAGE_PREVIEW_SIZE }}>
                    <ImageUpload
                      label="Image"
                      compact
                      squareAspect
                      squareSize={DISH_IMAGE_PREVIEW_SIZE}
                      accept={DISH_IMAGE_ACCEPT}
                      dimensionHint={DISH_IMAGE_DIMENSION_HINT}
                      formatHint={DISH_IMAGE_FORMAT_HINT}
                      validateImage={validateDishImageFile}
                      preview={dish.imagePreview}
                      error={Boolean(imageError)}
                      helperText={imageError}
                      onImageChange={(file, preview) => updateDishImage(dishIndex, file, preview)}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MenuCourseSection;
